// Обращение к ручке «Стан даних» (`GET /api/s-10/state`).
//
// ⚠⚠ **С таска 27 шапка сюда не ходит.** Свежесть шапки приходит своей ручкой,
// открытой каждой действующей учётной записи (`./api.ts`, решение оркестратора
// 84), — при `НВ` на `S-10` индикатор остаётся. Этот файл — единственная дверь к
// полному состоянию `S-10`, и **только он** разбирает ответ схемой `DataState`:
// журнал прогонов снимается в самом разборе (`readState`), а второй модуль,
// разобравший тот же ответ сам, получил бы журнал. Дверь держит сторож
// `tests/one-door-data-state.test.ts` (`carry.md`, строка таска 27).
//
// Прежний текст ниже говорит «каркас» — это о времени, когда шапка и `S-10`
// читали одно состояние; довод о журнале остаётся в силе целиком.
//
// ⚠⚠ **Почему в каркасе, а не в теке поверхности.** Индикатор свежести —
// сквозной признак **всех** поверхностей, а не элемент `S-10`
// (`docs/SCREEN-MAP.md:1018`): он живёт в шапке и виден на каждом экране.
// Поверхность `S-10` читает то же самое состояние, поэтому обращение одно и
// лежит там, где начинается — в каркасе. Второй запрос за тем же состоянием
// означал бы два ответа на один вопрос и мгновение, в которое шапка и экран
// говорят разное.
//
// ⚠⚠ **Слов индикатора здесь нет и быть не может.** Поле `indicator` приходит
// кодом случая (таск 72, решение оркестратора 106, пересмотревшее решение 83),
// а слово к коду — из словаря сервера (`@/api/words`): оно подставляется здесь,
// при разборе, и экран печатает его дословно. Ни таблицы соответствий, ни
// `switch` по случаю, ни копии пяти слов — их сторожит
// `tests/indicator-words.test.ts`.
//
// ⚠⚠ **Журнал прогонов до экрана не доходит вовсе, и держится это правилом, а
// не аккуратностью автора.** Карта держит блоки, которые он наполняет, «В
// розробці» (`docs/SCREEN-MAP.md:1052`), минимум называет только последнюю
// синхронизацию (`:1047`). Раньше запрет стерёг сканер исходников по виду
// записи — и был обойдён двумя обычными формами: переименованием при разборе
// (`const { runs: journal } = state`) и обращением по строке
// (`state["runs"][0]["requests"]`). Теперь журнал **снимается с состояния** до
// того, как оно уйдёт наверх: в типе `ShownState` поля нет, и любая форма
// обращения к нему — ошибка сборки, а не находка сканера.
//
// ⚠ **Срез 1б (таск 10): до экрана доходит последний прогон — выбором полей,
// а не журналом.** Карта 1б наполняет «Остання синхронізація» временем,
// длительностью и тем, что пришло (`docs/SCREEN-MAP.md`, `S-10`, «Срез 1б»),
// поэтому `ShownState` получил поле `last_run` (`LastRun` ниже, перечень полей
// назван там). Весь журнал `runs` по-прежнему снимается: его в типе нет, и
// сторож типов (`client/tests/typecheck-trials.test.ts`) держит это как раньше.

import { DataState, type SyncRunRow } from "@/api/schemas";
import { say, wordOf, wordsFor } from "@/api/words";

const BASE = "/api/s-10";

/** Ответ, который эта страница прочитать не смогла. Не прячется. */
export class AnswerUnreadable extends Error {}

/**
 * Возраст данных не сходится с моментом, от которого его считают.
 *
 * ⚠⚠ Отдельным классом затем, что это **не** «сервер прислал мусор», а
 * единственный числовой факт поверхности: `hours_ago` — подпись «скільки часу
 * дані не оновлювались» (`docs/SCREEN-MAP.md:1021`), число, которым человек
 * меряет возраст данных перед платежом. Показать его неверным хуже, чем не
 * показать: решение принимается по нему.
 */
export class FreshnessDefect extends AnswerUnreadable {}

/**
 * Насколько возраст вправе разойтись с меткой, в часах (5 минут).
 *
 * ⚠ Ноль здесь невозможен и не нужен: между сборкой ответа на сервере и его
 * разбором в браузере проходит время, а часы браузера и сервера не совпадают
 * никогда. Допуск выбран так, чтобы мимо не прошла подделка: разошедшийся на
 * пять минут возраст — это уже другое число на экране.
 */
const TOLERANCE_HOURS = 5 / 60;

/**
 * Состояние данных в том виде, в каком его видит экран: **без журнала прогонов**.
 *
 * ⚠⚠ Тип — и есть проверка. Пока `runs` лежал в типе, запрет держался сканером
 * исходников, а сканер держал форму записи: `state.runs[0].changed` он ловил,
 * `const { runs: journal } = state` и `state["runs"][0]["requests"]` — нет.
 * Поля, которого в типе не существует, не прочитать ни одной из этих форм:
 * каждая становится ошибкой `tsc`, и обойти её нечем.
 */
export type ShownState = Omit<DataState, "runs" | "indicator"> & {
  /** Слово индикатора из словаря сервера по коду случая. */
  readonly indicator: string;
  /** Код случая — по нему выбирается тон значка, а не слово. */
  readonly code?: DataState["indicator"];
  /**
   * Последний прогон — только то, что показывает блок «Остання синхронізація»
   * и «Ліміт запитів» среза 1б (`docs/SCREEN-MAP.md`, `S-10`, абзац «Срез 1б»:
   * «время, длительность, сколько документов пришло»; решение 164).
   */
  readonly last_run: LastRun | null;
};

/**
 * Поля последнего прогона, которые доходят до экрана (таск 10, отчёт до кода
 * п. 6; контракт `if1б-10.md`, «Дополнение после таска 10a»).
 *
 * ⚠ Перечень — выбор, а не весь журнал: `note_uk` прогона и
 * `retry_in_seconds` сюда не попадают. Первое повторяет подпись состояния
 * синхронизации, второе живьём не показывается (решение 165: пауза лимита
 * длится секунды и известна только внутри цикла).
 *
 * ⚠ `rows` — «прочитано рядків», не «прийшло документів» (`review-10a.md` Н3):
 * в число входит окно перечитывания и нахлёст по знаку.
 */
export type LastRun = Pick<
  SyncRunRow,
  "result" | "started_at" | "finished_at" | "seconds" | "going" | "rows"
  | "appeared" | "changed" | "disappeared" | "requests" | "slowed_by_limit"
>;

/**
 * Какой прогон показывается последним.
 *
 * ⚠ Журнал идёт новыми впереди и держит **и суточную сверку товаров**: удачный
 * прогон без счёта строк (`rows: null`) документов не читал — живой смоук
 * таска 10 показал его «останнім прогоном» с нулём изменений сразу после
 * первой синхронизации всей истории. Поэтому берётся первый прогон, который
 * идёт, не удался или считал строки; такого нет — самый новый. ⚠ Признака
 * «читал документы» у строки прогона нет (он есть у сервера, `_reads_documents`)
 * — правило выбора здесь временное и названо в отчёте таска 10.
 */
function pickRun(runs: DataState["runs"]): DataState["runs"][number] | undefined {
  return runs.find((one) => one.going || one.result !== "ok" || one.rows !== null) ?? runs[0];
}

function lastRunOf(state: DataState): LastRun | null {
  const run = pickRun(state.runs);
  if (run === undefined) return null;
  return {
    result: run.result,
    started_at: run.started_at,
    finished_at: run.finished_at ?? null,
    seconds: run.seconds ?? null,
    going: run.going,
    rows: run.rows,
    appeared: run.appeared,
    changed: run.changed,
    disappeared: run.disappeared,
    requests: run.requests,
    slowed_by_limit: run.slowed_by_limit,
  };
}

/**
 * Поля журнала прогонов — перечень, по которому сверяется **снятое**, а не
 * написанное (`schemas.py`, `SyncRunRow`).
 *
 * ⚠ `requests` здесь потому, что его в прежнем перечне не было вовсе: сканер
 * сторожил четыре имени из пяти, и пятое прошло бы даже в той форме, которую
 * он ловил.
 */
const RUNS_FIELDS = [
  "runs", "changed", "appeared", "disappeared", "requests", "slowed_by_limit",
] as const;

/** Журнал прогонов дошёл до экрана. Это дефект устройства, а не состояние данных. */
export class RunsShown extends AnswerUnreadable {}

/**
 * Состояние без журнала прогонов.
 *
 * ⚠ Сверка после снятия, а не вместо него: в срезе поле пусто всегда, но
 * пустота — свойство данных, а запрет — свойство устройства. Появится у ответа
 * ещё одно поле журнала верхним уровнем — оно не уедет на экран молча.
 */
export function withoutRuns(state: DataState, indicator: string): ShownState {
  const shown: Record<string, unknown> = {
    ...state,
    indicator,
    code: state.indicator,
    last_run: lastRunOf(state),
  };
  delete shown["runs"];
  const left = RUNS_FIELDS.filter((one) => one in shown);
  if (left.length > 0) {
    throw new RunsShown(
      `журнал прогонів дійшов до екрана полями: ${left.join(", ")}. Блоки, які він ` +
        "наповнює, стоять «В розробці», і показувати з них нічого.",
    );
  }
  return shown as ShownState;
}

/** Чем кончилось обращение за состоянием данных. */
export type StateAnswer =
  /** Состояние пришло и прочитано. ⚠ Журнала прогонов в нём нет по устройству. */
  | { readonly kind: "state"; readonly state: ShownState }
  /**
   * `НВ`: поверхность не выдана отметкой. Сервер не отдаёт ни одного факта, и
   * слова отказа пишет сам — имени пресета в них нет (решение 59).
   */
  | { readonly kind: "hidden"; readonly text_uk: string }
  /** Входа нет: до входа спрашивать состояние данных не у кого. */
  | { readonly kind: "no-session"; readonly text_uk: string | null }
  /** `ПМ`: не ответила сама поверхность. Текст — с сервера, дословно. */
  | { readonly kind: "cannot-show"; readonly text_uk: string }
  /** `ПМ`: система не ответила вовсе либо ответила нечитаемым. */
  | { readonly kind: "silent" };

function unreadable(where: string, cause: unknown): never {
  throw new AnswerUnreadable(`${where}: ответ не разобран схемой. ${String(cause)}`);
}

/** Тело ответа как объект. Не объект — ответ нечитаем. */
async function body(answer: Response, where: string): Promise<Record<string, unknown>> {
  let parsed: unknown;
  try {
    parsed = await answer.json();
  } catch (cause) {
    unreadable(where, cause);
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    unreadable(where, `тело ${typeof parsed}, ожидался объект`);
  }
  return parsed as Record<string, unknown>;
}

/**
 * Слова отказа. Ни одно из них не сочинено клиентом: отказ `S-10` и отказ входа
 * приходят кодом случая (слово — словарь сервера), отказ по праву `НВ` — пока
 * словами сервера (`api/rights.py`, вне таска 72).
 */
async function refusalWords(raw: Record<string, unknown>, where: string): Promise<string> {
  const kind = raw["kind"];
  if (typeof kind === "string" && kind.trim() !== "") return wordOf(kind);
  const said = raw["text_uk"];
  if (typeof said !== "string" || said.trim() === "") {
    unreadable(where, "в отказе нет ни kind, ни text_uk");
  }
  return said;
}

/**
 * Возраст данных сходится с меткой последней удачной синхронизации.
 *
 * Два правила, и каждое куплено: подделка ревьюера `hours_ago = 0.0` в рабочем
 * режиме прошла зелёной мимо всех проверок таска 14 (`carry.md`, таск 16).
 *
 * 1. **Возраста без метки не бывает.** `since` и `hours_ago` считаются от
 *    одного и того же последнего удачного прогона: есть одно — есть и второе.
 * 2. **Возраст равен времени, прошедшему с метки.** Расхождение больше допуска
 *    означает, что на экран идёт не тот возраст, по которому человек решает.
 */
export function assertAgeMatches(state: ShownState, now: Date = new Date()): void {
  const sync = state.sync;
  const ago = sync.hours_ago ?? null;
  const since = sync.since ?? null;

  if (since === null) {
    if (ago !== null) {
      throw new FreshnessDefect(
        `sync.hours_ago = ${ago}, а sync.since порожній: вік даних рахують від ` +
          "останньої вдалої синхронізації, і без неї віку не існує.",
      );
    }
    return;
  }
  if (ago === null) {
    throw new FreshnessDefect(
      `sync.since = ${since}, а sync.hours_ago порожній: мітка є, а підпису ` +
        "«скільки часу дані не оновлювались» немає.",
    );
  }

  const stamped = new Date(since);
  if (Number.isNaN(stamped.getTime())) {
    throw new FreshnessDefect(`sync.since = ${since}: це не момент часу.`);
  }
  // ⚠ Здесь не считается ничего, что попадает на экран: экран печатает
  // серверное `hours_ago`. Считается **расхождение** — ровно затем, чтобы
  // неверное число на экран не попало. Денег в этой арифметике нет: `S-10`
  // денежных чисел не показывает вовсе.
  const elapsedHours = (now.getTime() - stamped.getTime()) / 3_600_000;
  if (Math.abs(elapsedHours - ago) > TOLERANCE_HOURS) {
    throw new FreshnessDefect(
      `sync.hours_ago = ${ago}, а від sync.since (${since}) минуло ` +
        `${elapsedHours.toFixed(4)} год. Вік даних розійшовся з міткою більше ніж ` +
        `на ${Math.round(TOLERANCE_HOURS * 60)} хв — на екран іде не той вік, ` +
        "за яким людина вирішує, платити зараз чи чекати.",
    );
  }
}

async function readState(raw: Record<string, unknown>, where: string): Promise<ShownState> {
  const parsed = DataState.safeParse(raw);
  if (!parsed.success) unreadable(where, parsed.error);
  const code = parsed.data.indicator;
  const indicator = say(await wordsFor([code]), code);
  // ⚠ Порядок: сначала снять журнал, потом сверять возраст. Наверх уходит ровно
  // то, что проверено, — и ничего сверх.
  const shown = withoutRuns(parsed.data, indicator);
  assertAgeMatches(shown);
  return shown;
}

// --- `GET /state` -------------------------------------------------------------

export async function askDataState(): Promise<StateAnswer> {
  const where = "GET /api/s-10/state";
  let answer: Response;
  try {
    answer = await fetch(`${BASE}/state`, { headers: { accept: "application/json" } });
  } catch {
    return { kind: "silent" };
  }
  if (answer.status === 401) {
    // ⚠ Слова «сесія закінчилась» — словарь сервера по коду случая, и они
    // показываются дословно; тела без кода не бывает, но и падать из-за
    // отсутствия слов здесь незачем — вход всё равно кончился.
    let said: string | null = null;
    try {
      said = await refusalWords(await body(answer, where), where);
    } catch {
      said = null;
    }
    return { kind: "no-session", text_uk: said };
  }
  const raw = await body(answer, where);
  // ⚠ `НВ` разбирается по коду ответа, а не по тексту: слова отказа сервер
  // пишет для страницы, а на месте перехода в шапке стоит своя строка карты
  // (`docs/SCREEN-MAP.md:1027`), и имени пресета в ней нет.
  if (answer.status === 403) return { kind: "hidden", text_uk: await refusalWords(raw, where) };
  if (answer.status === 503) {
    return { kind: "cannot-show", text_uk: await refusalWords(raw, where) };
  }
  if (!answer.ok) return { kind: "silent" };
  return { kind: "state", state: await readState(raw, where) };
}
